import React, { useRef, useState, useCallback, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import Button from '../../../../components/Form/Button'
import Select from '../../../../components/Form/Select'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../shared/redux/hooks'
import { uploadDocumentApi, replaceDocumentApi } from '../../../../apis/apis'
import { formatFileSize } from '../../../../util/string'

interface UploadDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  tenantAccountId: string
  onUploadSuccess: () => void
  mode?: 'upload' | 'replace'
  documentId?: string
  documentTypeOptions: Record<string, string>
}

const MAX_FILE_SIZE = 10 * 1024 * 1024

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  tenantAccountId,
  onUploadSuccess,
  mode = 'upload',
  documentId,
  documentTypeOptions,
}) => {
  const dispatch = useAppDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  const isReplace = mode === 'replace'

  const resetForm = useCallback(() => {
    abortRef.current?.abort()
    setSelectedFile(null)
    setIsUploading(false)
    setIsDragOver(false)
  }, [])

  useEffect(() => {
    if (isOpen) {
      resetForm()
    }
  }, [isOpen, resetForm])

  useEffect(() => {
    return () => { abortRef.current?.abort() }
  }, [tenantAccountId])

  const handleClose = useCallback(() => {
    if (isUploading) return
    resetForm()
    onClose()
  }, [isUploading, resetForm, onClose])

  const validateFile = useCallback(
    (file: File): boolean => {
      const isPdf = file.type === 'application/pdf' || (!file.type && file.name.toLowerCase().endsWith('.pdf'))
      if (!isPdf) {
        dispatch(pushToast({ message: 'Only PDF files are allowed', type: 'error' }))
        return false
      }
      if (file.size > MAX_FILE_SIZE) {
        dispatch(pushToast({ message: 'File size must be less than 10 MB', type: 'error' }))
        return false
      }
      return true
    },
    [dispatch]
  )

  const handleFileSelect = useCallback(
    (file: File) => {
      if (validateFile(file)) {
        setSelectedFile(file)
      }
    },
    [validateFile]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFileSelect(file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [handleFileSelect]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) handleFileSelect(file)
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setIsDragOver(false)
  }, [])

  const handleSubmit = useCallback(async (docType?: string) => {
    if (!selectedFile) return

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setIsUploading(true)
    try {
      if (isReplace && documentId) {
        await replaceDocumentApi(tenantAccountId, documentId, selectedFile, controller.signal)
      } else {
        const resolvedType = docType || Object.keys(documentTypeOptions)[0]
        if (!resolvedType) {
          dispatch(pushToast({ message: 'Document types not loaded yet. Please try again.', type: 'error' }))
          setIsUploading(false)
          return
        }
        await uploadDocumentApi(tenantAccountId, selectedFile, resolvedType, controller.signal)
      }
      if (controller.signal.aborted) return
      dispatch(pushToast({ message: `Document ${isReplace ? 'replaced' : 'uploaded'} successfully`, type: 'success' }))
      onUploadSuccess()
      resetForm()
      onClose()
    } catch (error: any) {
      if (controller.signal.aborted) return
      const message = error.response?.data?.message || `Failed to ${isReplace ? 'replace' : 'upload'} document`
      dispatch(pushToast({ message, type: 'error' }))
    } finally {
      if (!controller.signal.aborted) setIsUploading(false)
    }
  }, [selectedFile, tenantAccountId, documentId, isReplace, dispatch, onUploadSuccess, resetForm, onClose, documentTypeOptions])

  if (!isOpen) return null

  return (
    <CustomContentModal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-lg text-left w-full max-w-md">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {isReplace ? 'Replace Document' : 'Upload Document'}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 mb-4">
          {isReplace
            ? 'Select a new file to replace the current version. The previous version is retained for audit.'
            : 'Upload a new document to this tenant account.'}
        </p>

        <Formik
          initialValues={{ documentType: Object.keys(documentTypeOptions)[0] ?? '' }}
          enableReinitialize
          onSubmit={async (values) => {
            await handleSubmit(values.documentType)
          }}
        >
          {() => (
            <Form>
              {!isReplace && (
                <div className="mb-4">
                  <Field
                    component={Select}
                    name="documentType"
                    label="Document Type"
                    options={documentTypeOptions}
                    disabled={isUploading}
                  />
                </div>
              )}

              {/* File Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  File
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleInputChange}
                  className="hidden"
                />

                {!selectedFile ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragOver
                        ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 ring-2 ring-indigo-200 dark:ring-indigo-800'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/30">
                      <svg
                        className="h-7 w-7 text-indigo-500 dark:text-indigo-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                        />
                      </svg>
                    </div>
                    <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                      Drag and drop your file here, or{' '}
                      <span className="text-indigo-600 dark:text-indigo-400 font-medium">browse</span>
                    </p>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      PDF only, max 10 MB
                    </p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-slate-900 rounded-md px-3 py-2 border border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      disabled={isUploading}
                      aria-label="Remove file"
                      className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-4">
                <Button type="button" onClick={handleClose} color="slate" size={2} disabled={isUploading} className="w-28">
                  Cancel
                </Button>
                {isReplace ? (
                  <Button
                    type="button"
                    onClick={() => handleSubmit()}
                    size={2}
                    disabled={!selectedFile || isUploading}
                    isSaving={isUploading}
                    className="w-28 ml-2"
                  >
                    Replace
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size={2}
                    disabled={!selectedFile || isUploading || Object.keys(documentTypeOptions).length === 0}
                    isSaving={isUploading}
                    className="w-28 ml-2"
                  >
                    Upload
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </CustomContentModal>
  )
}

export default UploadDocumentModal
